import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export type ContactTurnstileHandle = { reset: () => void };

type Props = {
  sitekey: string;
  onToken: (token: string) => void;
  onError: () => void;
};

const ContactTurnstile = forwardRef<ContactTurnstileHandle, Props>(function ContactTurnstile(
  { sitekey, onToken, onError },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>();
  const callbacks = useRef({ onToken, onError });
  callbacks.current = { onToken, onError };

  useImperativeHandle(ref, () => ({
    reset() {
      callbacks.current.onToken('');
      if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
    },
  }));

  useEffect(() => {
    if (!sitekey) {
      callbacks.current.onError();
      return;
    }

    let active = true;
    let scriptFailed = false;
    let loadTimeout: number | undefined;
    const existingScript = document.getElementById('turnstile-script');
    const script = existingScript || document.createElement('script');
    const handleScriptError = () => {
      if (!active || scriptFailed) return;
      scriptFailed = true;
      window.clearTimeout(loadTimeout);
      script.removeEventListener('load', mount);
      script.removeEventListener('error', handleScriptError);
      script.remove();
      callbacks.current.onError();
    };
    const mount = () => {
      if (!active || scriptFailed || !containerRef.current || widgetIdRef.current) return;
      window.clearTimeout(loadTimeout);
      if (!window.turnstile) {
        handleScriptError();
        return;
      }
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey,
          callback: (token) => {
            if (active) callbacks.current.onToken(token);
          },
          'expired-callback': () => {
            if (active) callbacks.current.onToken('');
            if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
          },
          'error-callback': () => {
            if (active) {
              callbacks.current.onToken('');
              callbacks.current.onError();
            }
          },
        });
      } catch {
        callbacks.current.onError();
      }
    };
    script.addEventListener('load', mount);
    script.addEventListener('error', handleScriptError);
    if (window.turnstile) mount();
    else {
      loadTimeout = window.setTimeout(handleScriptError, 10_000);
      if (!existingScript) {
        script.id = 'turnstile-script';
        script.setAttribute(
          'src',
          'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
        );
        script.setAttribute('async', '');
        document.head.appendChild(script);
      }
    }

    return () => {
      active = false;
      window.clearTimeout(loadTimeout);
      script.removeEventListener('load', mount);
      script.removeEventListener('error', handleScriptError);
      if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current);
      widgetIdRef.current = undefined;
    };
  }, [sitekey]);

  return (
    <div
      ref={containerRef}
      role='group'
      aria-label='Human verification'
    />
  );
});

export default ContactTurnstile;
